import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  IconButton,
  Skeleton,
  Spacer,
  Tooltip,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { UploadFileInput } from "src/components/Inputs/UploadFileInput";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { Table } from "src/components/Table/Table";
import useAccessToken from "src/hooks/useAccessToken";
import { AvelaToast, useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import * as BC from "src/services/breadcrumb";
import { getContentType } from "src/services/contentType";
import { Env, useEnv } from "src/services/env";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import { AuthData, Status } from "src/types/authData";
import * as RD from "src/types/remoteData";
import { z, ZodError } from "zod";
import {
  DataConfig,
  DataConfigJsonSchema,
  DataConfigSchema,
  UIConfig,
  UIConfigJsonSchema,
  UIConfigSchema,
} from "./types";
import { GeoJSONSchema } from "./types/geojsonSchema";

/*
  network calls
*/
async function fetchConfig(
  env: Env,
  accessToken: AuthData<string>,
  explorePath: string,
  fileName: "UIConfig.json" | "dataConfig.json"
): Promise<object> {
  if (accessToken.status !== Status.OK) {
    throw new Error("Missing access token");
  }
  const response = await fetch(
    `${env.VITE_EXPLORE_SERVICE_URL}/files/config/${explorePath}/${fileName}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken.data}`,
      },
    }
  );
  const json = await response.json();
  return json;
}

const ListAssetsResponseSchema = z.object({
  keys: z.array(z.string()),
});
type ListAssetsResponse = z.infer<typeof ListAssetsResponseSchema>;

async function listAssets(
  env: Env,
  accessToken: AuthData<string>,
  explorePath: string
): Promise<ListAssetsResponse> {
  if (accessToken.status !== Status.OK) {
    throw new Error("Missing access token");
  }
  const response = await fetch(
    `${env.VITE_EXPLORE_SERVICE_URL}/files/listAssets/${explorePath}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken.data}`,
      },
    }
  );
  const payload = await response.json();

  return ListAssetsResponseSchema.parse(payload);
}

type ConfigFile =
  | {
      name: "UIConfig.json";
      file: UIConfig;
    }
  | { name: "dataConfig.json"; file: DataConfig };

async function updateConfig(
  env: Env,
  accessToken: AuthData<string>,
  explorePath: string,
  file: ConfigFile
): Promise<void> {
  if (accessToken.status !== Status.OK) {
    throw new Error("Missing access token");
  }

  const response = await fetch(
    `${env.VITE_EXPLORE_SERVICE_URL}/files/config/${explorePath}/${file.name}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken.data}`,
      },
      body: JSON.stringify(file.file),
    }
  );

  if (!response.ok) {
    throw new Error(response.statusText, { cause: await response.json() });
  }
}

const UploadResponseSchema = z.object({
  url: z.string(),
  expiresIn: z.number(),
});

const uploadGeoJSON = async (
  env: Env,
  explorePath: string,
  token: AuthData<string>,
  key: string,
  file: File,
  setLoading: (flag: boolean) => void,
  toast: AvelaToast
): Promise<void> => {
  if (token.status !== Status.OK)
    throw new Error("uploadGeoJSON failed because of invalid token.");

  try {
    const reader = new FileReader();
    const fileContents: string = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });

    GeoJSONSchema.parse(JSON.parse(fileContents));

    setLoading(true);
    const response = await fetch(
      `${env.VITE_EXPLORE_SERVICE_URL}/files/generateUploadUrl/${explorePath}`,
      {
        method: "POST",
        body: JSON.stringify({
          key: key,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.data}`,
        },
      }
    );
    const { url } = UploadResponseSchema.parse(await response.json());
    const extension = key.split(".").pop();
    const uploadResponse = await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": getContentType(extension),
      },
    });
    if (!uploadResponse.ok)
      throw new Error(`upload ${key} returned ${uploadResponse.statusText}`);
    setLoading(false);

    toast({
      title: "Configuration uploaded",
      description: `File location: ${key}`,
      status: "info",
    });
  } catch (err: unknown) {
    setLoading(false);
    let message = "";

    if (err instanceof ZodError) {
      message = `Error in GeoJSON file. Please see browser console.`;
      console.error(err.message);
    } else if (err instanceof Error)
      message = `Failed to upload GeoJSON with: "${err.message}"`;
    else message = "Failed to upload GeoJSON with unkown error.";

    console.error(message);
    toast.error({
      title: "GeoJSON could not be uploaded",
      description: message,
    });
  }
};

const deleteGeoJSON = async (
  env: Env,
  explorePath: string,
  token: AuthData<string>,
  key: string,
  setLoading: (flag: boolean) => void,
  toast: AvelaToast
) => {
  if (token.status !== Status.OK)
    throw new Error("uploadGeoJSON failed because of invalid token.");

  try {
    setLoading(true);
    const response = await fetch(
      `${env.VITE_EXPLORE_SERVICE_URL}/files/generateDeleteUrl/${explorePath}`,
      {
        method: "POST",
        body: JSON.stringify({
          key: key,
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.data}`,
        },
      }
    );
    const { url } = UploadResponseSchema.parse(await response.json());
    const extension = key.split(".").pop();
    const uploadResponse = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": getContentType(extension),
      },
    });
    if (!uploadResponse.ok)
      throw new Error(`delete ${key} returned ${uploadResponse.statusText}`);
    setLoading(false);
    toast({
      title: "Configuration deleted",
      description: `File location: ${key}`,
      status: "info",
    });
  } catch (err: unknown) {
    setLoading(false);

    let message = "";
    if (err instanceof Error)
      message = `Failed to delete GeoJSON with: "${err.message}"`;
    else message = "Failed to delete GeoJSON with unkown error.";

    console.error(message);
    toast.error({
      title: "GeoJSON could not be deleted",
      description: message,
    });
  }
};

/*
  hooks
*/

type UseExploreProps = {
  organizationId: string | undefined;
  explorePath: string | undefined;
};

type ExploreConfig = {
  uiConfig: unknown;
  dataConfig: unknown;
};

type ReturnType = {
  remoteData: RD.RemoteData<Error, ExploreConfig>;
  update: (
    uiConfig: UIConfig | "NoChange",
    dataConfig: DataConfig | "NoChange"
  ) => Promise<void>;
};

const useExploreConfig = ({
  explorePath,
  organizationId,
}: UseExploreProps): ReturnType => {
  const accessToken = useAccessToken();
  const [remoteData, setRemoteData] = useState<
    RD.RemoteData<Error, ExploreConfig>
  >(RD.loading());

  const env = useEnv();

  useEffect(() => {
    const goFetch = async () => {
      if (accessToken.status === Status.LOADING) {
        return;
      }

      if (!explorePath) {
        throw new Error("Missing explore path");
      }

      try {
        const [uiConfig, dataConfig] = await Promise.all([
          fetchConfig(env, accessToken, explorePath, "UIConfig.json"),
          fetchConfig(env, accessToken, explorePath, "dataConfig.json"),
        ]);
        setRemoteData(RD.success({ uiConfig, dataConfig }));
      } catch (error) {
        setRemoteData(
          RD.failure(
            error instanceof Error
              ? error
              : new Error("Unknown error", { cause: error })
          )
        );
      }
    };

    goFetch();
  }, [accessToken, env, explorePath]);

  const update = useCallback(
    async (
      uiConfig: UIConfig | "NoChange",
      dataConfig: DataConfig | "NoChange"
    ) => {
      if (!explorePath) {
        throw new Error("Missing explore path");
      }

      await Promise.all([
        uiConfig === "NoChange"
          ? undefined
          : updateConfig(env, accessToken, explorePath, {
              name: "UIConfig.json",
              file: uiConfig,
            }),
        dataConfig === "NoChange"
          ? undefined
          : updateConfig(env, accessToken, explorePath, {
              name: "dataConfig.json",
              file: dataConfig,
            }),
      ]);
    },
    [accessToken, env, explorePath]
  );

  if (!organizationId) {
    return {
      remoteData: RD.failure(new Error("Missing organizationId")),
      update,
    };
  }
  if (!explorePath) {
    return { remoteData: RD.failure(new Error("Missing explorePath")), update };
  }

  return { remoteData, update };
};

/*
  components
*/

const Loading: React.FC = () => {
  return (
    <Flex gap={4} direction="column" width="100%">
      <Skeleton height="3rem" width="28rem" />
      <Spacer />
      <Spacer />

      <Skeleton height="3rem" width="20rem" />

      <Skeleton height="1.5rem" width="15rem" />
      <Skeleton height="10rem" width="100%" />

      <Skeleton height="1.5rem" width="15rem" />
      <Skeleton height="10rem" width="100%" />
    </Flex>
  );
};

export const EditExplore: React.FC = () => {
  const { explorePath } = useParams();
  const organization = useOrganization();
  const toast = useAvelaToast();
  const navigate = useNavigate();
  const [isSaving, setSaving] = useState(false);
  const token = useAccessToken();
  const env = useEnv();

  const prefixRegex = new RegExp("output\\/[^\\/]+\\/");

  const { remoteData: configRemoteData, update: updateConfig } =
    useExploreConfig({
      organizationId: organization.toNullable()?.id, // TODO
      explorePath,
    });

  const [listAssetsRemoteData, setListAssetsRemoteData] = useState<
    RD.RemoteData<Error, ListAssetsResponse>
  >(RD.loading());

  const [isAssetChanging, setIsAssetChanging] = useState<boolean>(false);

  if (!explorePath) {
    throw new Error("Missing explore path");
  }

  const refreshListAssets = useCallback(async () => {
    const resp = await listAssets(env, token, explorePath);
    setListAssetsRemoteData(RD.success(resp));
  }, [env, explorePath, token]);

  useEffect(() => {
    if (token.status === Status.LOADING) return;
    refreshListAssets();
  }, [token, refreshListAssets]);

  return (
    <Flex minHeight="100%" direction="column" gap={6}>
      <Breadcrumb
        items={BC.organization.getBreadcrumbsForEditExplore(
          organization,
          explorePath
        )}
      />
      <Heading as="h1" variant="admin">
        Edit {explorePath}
      </Heading>
      <RemoteDataView
        remoteData={configRemoteData}
        error={(error) => <GenericError error={error} />}
        loading={<Loading />}
      >
        {(data) => (
          <Formik<{ uiConfig: string; dataConfig: string }>
            initialValues={{
              uiConfig: JSON.stringify(data.uiConfig, null, 2),
              dataConfig: JSON.stringify(data.dataConfig, null, 2),
            }}
            validate={(values) => {
              const uiConfig = _.attempt(() => JSON.parse(values.uiConfig));
              const dataConfig = _.attempt(() => JSON.parse(values.dataConfig));
              return validateWithZod(
                z.object({
                  uiConfig: UIConfigSchema,
                  dataConfig: DataConfigSchema,
                })
              )({
                uiConfig,
                dataConfig,
              });
            }}
            onSubmit={async (values: {
              uiConfig: string;
              dataConfig: string;
            }) => {
              try {
                if (!organization.hasData()) {
                  throw new Error("Missing organizationId");
                }

                const uiConfig = UIConfigSchema.safeParse(
                  JSON.parse(values.uiConfig)
                );
                if (uiConfig.error) {
                  throw new Error("Invalid UI settings", {
                    cause: uiConfig.error,
                  });
                }
                const dataConfig = DataConfigSchema.safeParse(
                  JSON.parse(values.dataConfig)
                );
                if (dataConfig.error) {
                  throw new Error("Invalid data configuration", {
                    cause: dataConfig.error,
                  });
                }

                const uiConfigHasChanges = !_.isEqual(
                  data.uiConfig,
                  uiConfig.data
                );
                const dataConfigHasChanges = !_.isEqual(
                  data.dataConfig,
                  dataConfig.data
                );

                if (uiConfigHasChanges || dataConfigHasChanges) {
                  setSaving(true);
                  await updateConfig(
                    uiConfigHasChanges ? uiConfig.data : "NoChange",
                    dataConfigHasChanges ? dataConfig.data : "NoChange"
                  );
                  toast({
                    title: "Explore instance updated",
                    status: "success",
                  });
                }

                navigate(
                  Url.OrgAdmin.Org.settings(
                    organization.data,
                    "licenses-and-billing"
                  )
                );
                setSaving(false);
              } catch (error) {
                setSaving(false);
                if (error instanceof Error) {
                  toast.error({
                    title: "Instance could not be updated",
                    description: error.message,
                  });
                } else {
                  toast.error({
                    title: "Instance could not be updated",
                    description: "Unknown error",
                  });
                }
                console.error(error);
              }
            }}
          >
            {(formik) => {
              const uiConfigMeta = formik.getFieldMeta("uiConfig");
              const dataConfigMeta = formik.getFieldMeta("dataConfig");
              return (
                <Flex as={Form} direction="column" gap={10} flexGrow={1}>
                  <Flex direction="column" gap={4}>
                    <Heading as="h2" variant="admin">
                      Instance configuration
                    </Heading>
                    <Accordion defaultIndex={[0, 1]} allowMultiple>
                      <AccordionItem
                        as={FormControl}
                        isInvalid={uiConfigMeta.error !== undefined}
                      >
                        {(row) => {
                          const error = (
                            <FormErrorMessage>
                              Invalid ui settings
                            </FormErrorMessage>
                          );
                          return (
                            <>
                              <AccordionButton justifyContent="space-between">
                                <Flex
                                  direction="column"
                                  alignItems="flex-start"
                                >
                                  <Box fontSize="md">UI settings</Box>
                                  {!row.isExpanded && error}
                                </Flex>
                                <AccordionIcon />
                              </AccordionButton>

                              <AccordionPanel>
                                <JsonEditor
                                  schema={UIConfigJsonSchema}
                                  text={formik.values.uiConfig}
                                  height="20rem"
                                  onChange={(uiConfig) => {
                                    formik.setFieldValue("uiConfig", uiConfig);
                                  }}
                                  filename={`uiConfig.${explorePath}.json`}
                                  editorOptions={{ readOnly: isSaving }}
                                  hideToolbar
                                  editorHeight="resizable"
                                />
                                {error}
                              </AccordionPanel>
                            </>
                          );
                        }}
                      </AccordionItem>
                      <AccordionItem
                        as={FormControl}
                        isInvalid={dataConfigMeta.error !== undefined}
                      >
                        {(row) => {
                          const error = (
                            <FormErrorMessage>
                              Invalid data configuration
                            </FormErrorMessage>
                          );
                          return (
                            <>
                              <AccordionButton justifyContent="space-between">
                                <Flex
                                  direction="column"
                                  alignItems="flex-start"
                                >
                                  <Box fontSize="md">Data configuration</Box>
                                  {!row.isExpanded && error}
                                </Flex>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel>
                                <JsonEditor
                                  schema={DataConfigJsonSchema}
                                  text={formik.values.dataConfig}
                                  height="20rem"
                                  onChange={(dataConfig) => {
                                    formik.setFieldValue(
                                      "dataConfig",
                                      dataConfig
                                    );
                                  }}
                                  filename={`dataConfig.${explorePath}.json`}
                                  editorOptions={{ readOnly: isSaving }}
                                  hideToolbar
                                  editorHeight="resizable"
                                />
                                {error}
                              </AccordionPanel>
                            </>
                          );
                        }}
                      </AccordionItem>
                    </Accordion>
                  </Flex>
                  <RemoteDataView
                    remoteData={listAssetsRemoteData}
                    error={(error) => <GenericError error={error} />}
                    loading={<Loading />}
                  >
                    {(data) => (
                      <Flex direction="column" gap={4}>
                        <Heading as="h2" variant="admin">
                          Boundaries
                        </Heading>
                        <Flex minWidth="100%" justifyContent="right">
                          <Tooltip label="Upload GeoJSON" hasArrow>
                            <UploadFileInput
                              disabled={isAssetChanging}
                              buttonLabel="Upload GeoJSON"
                              accept=".geojson"
                              onUpload={(f: File) => {
                                uploadGeoJSON(
                                  env,
                                  explorePath,
                                  token,
                                  `output/${explorePath}/${f.name}`,
                                  f,
                                  (flag: boolean) => {
                                    setIsAssetChanging(flag);
                                    refreshListAssets();
                                  },
                                  toast
                                );
                              }}
                            />
                          </Tooltip>
                        </Flex>
                        <Table
                          tableHeader={() => <></>}
                          data={data.keys
                            .filter((x) => x.includes(".geojson"))
                            .map((x) => ({
                              key: x,
                              name: x.replace(prefixRegex, ""),
                            }))}
                          columns={[
                            {
                              id: "1",
                              header: "Instance",
                              accessorFn: (row) => row.name,
                              size: 1000,
                            },
                            {
                              id: "2",
                              header: "Actions",
                              cell: ({ row }) => {
                                return (
                                  <Flex
                                    gap={2}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                  >
                                    <Tooltip label="Delete file" hasArrow>
                                      <IconButton
                                        aria-label="Delete file"
                                        variant="outline"
                                        colorScheme="gray"
                                        icon={<RiDeleteBin6Line />}
                                        isLoading={isAssetChanging}
                                        isDisabled={isAssetChanging}
                                        onClick={() => {
                                          deleteGeoJSON(
                                            env,
                                            explorePath,
                                            token,
                                            row.original.key,
                                            (flag: boolean) => {
                                              setIsAssetChanging(flag);
                                              refreshListAssets();
                                            },
                                            toast
                                          );
                                        }}
                                      />
                                    </Tooltip>
                                  </Flex>
                                );
                              },
                            },
                          ]}
                        />
                      </Flex>
                    )}
                  </RemoteDataView>

                  <Spacer />
                  <AdminFormButtons sticky alignItems="center">
                    <Button
                      as={NavLink}
                      to={organization
                        .map((org) =>
                          Url.OrgAdmin.Org.settings(org, "licenses-and-billing")
                        )
                        .withDefault("#")}
                      variant="outline"
                      colorScheme="gray"
                    >
                      Cancel
                    </Button>
                    <Spacer />
                    <Button
                      type="submit"
                      isLoading={isSaving || isAssetChanging}
                      isDisabled={!formik.isValid}
                    >
                      Update instance
                    </Button>
                  </AdminFormButtons>
                </Flex>
              );
            }}
          </Formik>
        )}
      </RemoteDataView>
    </Flex>
  );
};
